/*
 * Shrine on Horizon — product page components.
 * Variant pickers, product info, bundle deals, quantity breaks, quantity gifts,
 * main bundle offer, custom product fields and the sticky add-to-cart bar.
 * Depends on helpers from sh-global.js. Safe to include more than once.
 */
(() => {
  if (customElements.get('variant-selects')) return;

  /* ---------- Product info (quantity rules) ---------- */

  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity__input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.submitButton = this.querySelector('[type="submit"]');
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;

      this.setQuantityBoundries();
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    setQuantityBoundries() {
      const data = {
        cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
        min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
        max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
        step: this.input.step ? parseInt(this.input.step) : 1,
      };
      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.input.min = min;
      if (max !== null) this.input.max = max;
      this.input.value = min;
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      const overlay = this.querySelector('.quantity__rules-cart .loading-overlay');
      overlay?.classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateQuantityRules(this.dataset.section, html);
          this.setQuantityBoundries();
        })
        .catch((e) => console.error(e))
        .finally(() => overlay?.classList.add('hidden'));
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      if (!quantityFormUpdated) return;
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (const selector of selectors) {
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          for (const attribute of ['data-cart-quantity', 'data-min', 'data-max', 'step']) {
            const value = updated.getAttribute(attribute);
            if (value !== null) current.setAttribute(attribute, value);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- Variant pickers ---------- */

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.secondarySelectSelector = 'StickyAtcVariantPicker-';
      this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
      this.isSecondary = false;
      this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
      this.hasQuantityBreaksPicker = this.dataset.hasQuantityBreaksPicker === 'true';
      this.prependMedia = this.dataset.disablePrepend != 'true';
      this.filtering = this.dataset.hasFiltering === 'true';
      this.skipNonExistent = this.dataset.skipNonExistent === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      if (this.hasQuantityBreaksPicker) {
        this.quantityBreaksPickerStyle = this.dataset.quantityBreaksPickerStyle;
        this.quantityBreaksPickerDisplayedImages = this.dataset.quantityBreaksPickerDisplayedImages;
      }
      this.addEventListener('change', this.onVariantChange);
    }

    get sourceSection() {
      return this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantStatuses();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        if (this.skipNonExistent && findAvailableVariant(this, this.options, true, true, this.skipUnavailable)) return;
        this.setUnavailable();
        return;
      }
      if (this.skipUnavailable && !this.currentVariant.available) {
        if (findAvailableVariant(this, this.options, true, true, true)) return;
      }
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }

    updateOptions() {
      const options = [];
      this.querySelectorAll('.product-form__input').forEach((wrapper) => {
        const type = wrapper.querySelector('.product-form__input__type').dataset.type;
        if (type == 'dropdown' || type == 'dropdwon') {
          options.push(wrapper.querySelector('.select__select').value);
        } else {
          options.push(wrapper.querySelector('input[type="radio"]:checked').value);
        }
      });
      this.options = options;
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find(
        (variant) => !variant.options.map((option, index) => this.options[index] === option).includes(false)
      );
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;
      document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`).forEach((gallery) =>
        gallery.setActiveMedia?.(
          `${this.dataset.section}-${this.currentVariant.featured_media.id}`,
          this.prependMedia,
          this.filtering,
          this.currentVariant
        )
      );

      const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      if (!modalContent) return;
      const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      if (newMediaModal) modalContent.prepend(newMediaModal);
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton || !shareButton.updateUrl) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const forms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      );
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateVariantStatuses() {
      const firstChecked = this.querySelector(':checked');
      if (!firstChecked) return;
      const selectedOptionOneVariants = this.getVariantData().filter((variant) => firstChecked.value === variant.option1);
      const inputWrappers =
        this.isSecondary && this.secondarySelect
          ? [...this.secondarySelect.querySelectorAll('.product-form__input')]
          : [...this.querySelectorAll('.product-form__input')];

      inputWrappers.forEach((option, index) => {
        if (index === 0) return;
        const optionInputs = [...option.querySelectorAll('input[type="radio"], .select__select option')];
        const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked')?.value;
        const availableOptionInputsValue = selectedOptionOneVariants
          .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
          .map((variantOption) => variantOption[`option${index + 1}`]);
        const existingOptionInputsValues = selectedOptionOneVariants
          .filter((variant) => variant[`option${index}`] === previousOptionSelected)
          .map((variantOption) => variantOption[`option${index + 1}`]);
        this.setInputAvailability(optionInputs, availableOptionInputsValue, existingOptionInputsValues);
      });
    }

    setInputAvailability(elementList, availableValuesList, existingValuesList) {
      elementList.forEach((element) => {
        element.classList.remove('unavailable', 'non-existent', 'disabled');
        const value = element.getAttribute('value');
        const swatch = element.dataset.swatch === 'true' ? element.closest('.color-swatch') : null;
        swatch?.classList.remove('unavailable', 'non-existent', 'disabled');
        const label = window.variantStrings.unavailable_with_option.replace('[value]', value);

        if (existingValuesList.includes(value)) {
          if (availableValuesList.includes(value)) {
            if (element.nodeName === 'OPTION') element.innerText = value;
          } else if (element.nodeName === 'OPTION') {
            element.innerText = label;
            element.classList.add('unavailable');
          } else {
            element.classList.add('disabled', 'unavailable');
            swatch?.classList.add('disabled', 'unavailable');
          }
        } else if (element.nodeName === 'OPTION') {
          element.innerText = label;
          element.classList.add('non-existent');
        } else {
          element.classList.add('disabled', 'non-existent');
          swatch?.classList.add('disabled', 'non-existent');
        }
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;
      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability?.(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;
      section.querySelector('product-form')?.handleErrorMessage();
    }

    renderProductInfo() {
      const requestedVariantId = this.currentVariant.id;
      const sourceSection = this.sourceSection;
      const section = this.dataset.section;

      fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sourceSection}`)
        .then((response) => response.text())
        .then((responseText) => {
          // Ignore stale responses when the shopper clicked through variants quickly.
          if (this.currentVariant.id !== requestedVariantId) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          const swapInner = (currentId, sourceId) => {
            const current = document.getElementById(currentId);
            const source = html.getElementById(sourceId);
            if (current && source) current.innerHTML = source.innerHTML;
            return [current, source];
          };

          swapInner(`price-${section}`, `price-${sourceSection}`);
          swapInner(`sticky-atc-separate-price-${section}`, `sticky-atc-separate-price-${sourceSection}`);
          swapInner(`sticky-atc-price-${section}`, `sticky-atc-price-${sourceSection}`);
          swapInner(`main-atc-price-${section}`, `main-atc-price-${sourceSection}`);

          const stickyImage = document.getElementById(`sticky-atc-image-${section}`);
          const stickyImageSource = html.getElementById(`sticky-atc-image-${sourceSection}`);
          if (stickyImage && stickyImageSource) stickyImage.src = stickyImageSource.src;

          const customLabels = document.querySelectorAll(`[id^="custom-label-${section}"]`);
          const customLabelsSource = html.querySelectorAll(`[id^="custom-label-${sourceSection}"]`);
          customLabels.forEach((label, i) => {
            if (customLabelsSource[i]) label.innerHTML = customLabelsSource[i].innerHTML;
          });

          const [inventory, inventorySource] = swapInner(`Inventory-${section}`, `Inventory-${sourceSection}`);

          const skuSource = html.getElementById(`Sku-${sourceSection}`);
          const sku = document.getElementById(`Sku-${section}`);
          if (sku && skuSource) {
            sku.innerHTML = skuSource.innerHTML;
            sku.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
          }

          if (this.quantityBreaks) {
            const quantityBreaksSource = html.getElementById(`quantity-breaks-${sourceSection}`);
            if (quantityBreaksSource) {
              const prices = this.quantityBreaks.querySelectorAll('.dynamic-price');
              const pricesSource = quantityBreaksSource.querySelectorAll('.dynamic-price');
              prices.forEach((price, i) => {
                if (pricesSource[i]) price.innerHTML = pricesSource[i].innerHTML;
              });
              if (this.quantityBreaks.hasVariants) {
                this.quantityBreaks.variantSelects.forEach((select) => {
                  select.dataset.selectedId = this.currentVariant.id;
                });
                const selects = this.quantityBreaks.querySelectorAll('.quantity-break__variant-select');
                const selectsSource = quantityBreaksSource.querySelectorAll('.quantity-break__variant-select');
                selects.forEach((select, i) => {
                  if (selectsSource[i]) select.innerHTML = selectsSource[i].innerHTML;
                });
                this.quantityBreaks.initVariants();
              }
            }
          }

          if (this.hasQuantityBreaksPicker) {
            const pickerSource = html.getElementById(`variant-selects-${sourceSection}`);
            if (pickerSource) {
              const prices = this.querySelectorAll('.dynamic-price');
              const pricesSource = pickerSource.querySelectorAll('.dynamic-price');
              prices.forEach((price, i) => {
                if (pricesSource[i]) price.innerHTML = pricesSource[i].innerHTML;
              });
              if (this.quantityBreaksPickerStyle === 'vertical' && this.quantityBreaksPickerDisplayedImages === 'variant_images') {
                const images = this.querySelectorAll('.quantity-break__image img');
                const imagesSource = pickerSource.querySelectorAll('.quantity-break__image img');
                images.forEach((image, i) => {
                  if (imagesSource[i]) image.src = imagesSource[i].src;
                });
              }
            }
          }

          if (this.secondarySelect) {
            const secondarySource = html.getElementById(`${this.secondarySelectSelector}${sourceSection}`);
            if (secondarySource) this.secondarySelect.innerHTML = secondarySource.innerHTML;
          }

          document.getElementById(`price-${section}`)?.classList.remove('visibility-hidden');
          this.dataset.options = this.currentVariant.options.join(',');
          if (inventory && inventorySource) {
            inventory.classList.toggle('visibility-hidden', inventorySource.innerText === '');
          }

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sourceSection}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: sourceSection, html, variant: this.currentVariant },
          });
        });
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;
      const extraButtons = document.querySelectorAll('.main-product-atc');
      const addButtonText =
        productForm.querySelector('.main-atc__label__text') || productForm.querySelector('.main-atc__label');
      const atcPrice = productForm.querySelector('.main-atc-price');

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        addButton.setAttribute('data-unavailable', 'true');
        if (text && addButtonText) addButtonText.textContent = text;
        extraButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
        atcPrice?.classList.add('hidden');
      } else {
        addButton.setAttribute('data-unavailable', 'false');
        if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
        atcPrice?.classList.remove('hidden');
        if (addButton.dataset.requiredFields === addButton.dataset.validFields) {
          addButton.removeAttribute('disabled');
          extraButtons.forEach((button) => button.removeAttribute('disabled'));
        }
      }
      if (!modifyClass) return;
    }

    setUnavailable() {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;
      const addButtonText =
        productForm.querySelector('.main-atc__label__text') || productForm.querySelector('.main-atc__label');
      if (addButtonText) addButtonText.textContent = window.variantStrings.unavailable;
      ['price', 'Inventory', 'Sku'].forEach((prefix) => {
        document.getElementById(`${prefix}-${this.dataset.section}`)?.classList.add('visibility-hidden');
      });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  class SecondaryVariantSelect extends VariantSelects {
    constructor() {
      super();
      this.secondarySelectSelector = 'variant-selects-';
      this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
      this.isSecondary = true;
    }

    updateOptions() {
      this.options = this.querySelector('select').value.split(',');
    }
  }
  customElements.define('secondary-variant-select', SecondaryVariantSelect);

  class SecondaryVariantSelectSeparate extends VariantSelects {
    constructor() {
      super();
      this.secondarySelectSelector = 'variant-selects-';
      this.secondarySelect = document.getElementById(`${this.secondarySelectSelector}${this.dataset.section}`);
      this.isSecondary = true;
    }
  }
  customElements.define('secondary-variant-select-separate', SecondaryVariantSelectSeparate);

  class CardVariantSelects extends VariantSelects {
    constructor() {
      super();
      this.quickAddSubmit = document.getElementById(`${this.dataset.section}-submit`);
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updateVariantStatuses();
      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateVariantInput();
        this.updateQuickAddUrl();
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      }
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;
      const image = document.getElementById(`${this.dataset.section}-image`);
      if (!image) return;
      const preview = this.currentVariant.featured_media.preview_image;
      const src = preview.src.split('?')[0];
      const widths = [165, 360, 533, 720, 940, 1066];
      const srcset = widths
        .filter((width) => preview.width >= width)
        .map((width) => `${src}?width=${width} ${width}w`)
        .join(', ');
      image.srcset = `${srcset}, ${src} ${preview.width}w`;
      image.src = `${src}?width=533`;
    }

    updateQuickAddUrl() {
      if (!this.currentVariant || !this.quickAddSubmit) return;
      this.quickAddSubmit.dataset.productUrl = `${this.dataset.url}?variant=${this.currentVariant.id}`;
    }
  }
  customElements.define('card-variant-selects', CardVariantSelects);

  /* ---------- Bundle deals section ---------- */

  class BundleDeals extends HTMLElement {
    constructor() {
      super();
      this.productContainers = this.querySelectorAll('.bundle-deals__product-js');
      this.mediaItemContainers = this.querySelectorAll('.bundle-deals__media-item-container-js');
      this.mediaItemImgs = this.querySelectorAll('.bundle-deals__media-item-img-js');
      this.checkboxes = this.querySelectorAll('.bundle-deals__checkbox-js');
      this.variantPickers = this.querySelectorAll('.bundle-deals__variant-selects-js');
      this.skipNonExistent = this.dataset.skipNonExistent === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this.prices = this.querySelectorAll('.bundle-deals__price-js');
      this.comparePrices = this.querySelectorAll('.bundle-deals__compare-price-js');
      this.totalPrice = this.querySelector('.bundle-deals__total-price-js');
      this.totalComparePrice = this.querySelector('.bundle-deals__total-compare-price-js');
      this.updatePrices = this.dataset.updatePrices === 'true';
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
      this.currencySymbol = this.dataset.currencySymbol;
      this.moneyFormat = this.dataset.moneyFormat;
      this.selectedVariants = { id_1: null, id_2: null, id_3: null, id_4: null, id_5: null };
      this.formVariants = [];
      this.initIds();
      this.checkboxes.forEach((checkbox) => checkbox.addEventListener('change', this.handleCheckboxChange.bind(this)));
      this.variantPickers.forEach((picker) => picker.addEventListener('change', this.handleSelectChange.bind(this)));
    }

    money(cents) {
      return this.moneyFormat ? formatMoney(cents, this.moneyFormat) : `${this.currencySymbol}${(cents / 100).toFixed(2)}`;
    }

    initIds() {
      this.checkboxes.forEach((checkbox) => {
        this.selectedVariants[checkbox.dataset.idIndex] = {
          id: checkbox.dataset.id,
          price: checkbox.dataset.price,
          comparePrice: checkbox.dataset.comparePrice,
          checked: true,
        };
      });
      this.updateFormIds();
    }

    handleCheckboxChange(event) {
      const checkbox = event.currentTarget;
      const checked = checkbox.checked;
      const index = parseInt(checkbox.dataset.index);
      this.selectedVariants[checkbox.dataset.idIndex].checked = checked;
      const container = this.productContainers[index];
      const selects = container.querySelectorAll('select');
      this.mediaItemContainers[index]?.classList.toggle('bundle-deals__media-item--disabled', !checked);
      container.classList.toggle('bundle-deals__product--deselected', !checked);
      selects.forEach((select) => (checked ? select.removeAttribute('disabled') : select.setAttribute('disabled', '')));
      this.updateFormIds();
      if (this.updatePrices) this.updateTotalPrice();
    }

    handleSelectChange(event) {
      const picker = event.currentTarget;
      const index = parseInt(picker.dataset.index);
      const options = Array.from(picker.querySelectorAll('select'), (select) => select.value);
      const variants = JSON.parse(picker.querySelector('[type="application/json"]').textContent);
      const sameFirstOption = variants.filter((variant) => picker.querySelector(':checked').value === variant.option1);
      updateVariantStatuses(sameFirstOption, [...picker.querySelectorAll('.select--small')]);

      const variant = variants.find((v) => !v.options.map((value, i) => options[i] === value).includes(false));
      if (!variant) {
        if (this.skipNonExistent) findAvailableVariant(picker, options, false, true, this.skipUnavailable);
        return;
      }
      if (this.skipUnavailable && !variant.available) {
        if (findAvailableVariant(picker, options, false, true, true)) return;
      }

      let price = parseInt(variant.price);
      const comparePrice = variant.compare_at_price ? parseInt(variant.compare_at_price) : price;
      const percentageLeft = picker.dataset.percentageLeft ?? 1;
      const fixedDiscount = picker.dataset.fixedDiscount ?? 0;
      price = price * percentageLeft - fixedDiscount;

      const selected = this.selectedVariants[picker.dataset.idIndex];
      selected.id = variant.id;
      selected.price = price;
      selected.comparePrice = comparePrice;
      this.updateFormIds();

      if (this.updatePrices) {
        this.prices[index].innerHTML = this.money(price);
        this.comparePrices[index].innerHTML = comparePrice > price ? this.money(comparePrice) : '';
        this.updateTotalPrice();
      }
      const image = variant.featured_image?.src;
      if (image && this.mediaItemImgs[index]) this.mediaItemImgs[index].src = image;
    }

    updateFormIds() {
      const variants = [];
      Object.values(this.selectedVariants).forEach((variant) => {
        if (variant == null || !variant.checked) return;
        const existing = variants.find((item) => item.id === variant.id);
        if (existing) {
          existing.quantity += 1;
        } else {
          variants.unshift({ id: variant.id, quantity: 1 });
        }
      });
      this.formVariants = variants;
    }

    updateTotalPrice() {
      const prices = [];
      const comparePrices = [];
      Object.values(this.selectedVariants).forEach((variant) => {
        if (variant == null || !variant.checked) return;
        prices.push(parseInt(variant.price));
        comparePrices.push(parseInt(variant.comparePrice));
      });
      const total = prices.reduce((sum, value) => sum + value, 0) * this.percentageLeft - this.fixedDiscount;
      const compareTotal = comparePrices.reduce((sum, value) => sum + value, 0);
      this.totalPrice.innerHTML = this.money(total);
      this.totalComparePrice.innerHTML = compareTotal > total ? this.money(compareTotal) : '';
    }
  }
  customElements.define('bundle-deals', BundleDeals);

  /* ---------- Quantity breaks ---------- */

  class QuantityBreaks extends HTMLElement {
    constructor() {
      super();
      this.quantityGifts = document.getElementById(`quantity-gifts-${this.dataset.section}`);
      this.inputs = this.querySelectorAll('input[name="quantity"]');
      this.labels = this.querySelectorAll('.quantity-break');
      this.jsonData = this.querySelector('[type="application/json"]');
      this.hasVariants = this.jsonData?.dataset.hasVariants === 'true';
      this.selectedVariants = { input_1: [], input_2: [], input_3: [], input_4: [], input_5: [], input_6: [] };
      this.updateUnavailable = this.dataset.updateUnavailable === 'true';
      this.skipNonExistent = this.dataset.skipNonExistent === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this.formVariants = [];
      this.selectedQuantity = 1;
      const checked = this.querySelector('input[checked]');
      if (checked) this.selectedQuantity = parseInt(checked.value);
      this.variantSelects = this.querySelectorAll('.quantity-break__selector-item');
      this.updatePrices = this.dataset.updatePrices === 'true';
      this.moneyFormat = this.dataset.moneyFormat;
      if (this.hasVariants) this.initVariants();
      this.inputs.forEach((input) => input.addEventListener('change', this.handleChange.bind(this)));
      this.variantSelects.forEach((select) => select.addEventListener('change', this.handleSelectChange.bind(this)));
    }

    handleSelectChange(event) {
      const picker = event.currentTarget;
      const options = Array.from(picker.querySelectorAll('select'), (select) => select.value);
      if (this.updateUnavailable) {
        const sameFirstOption = this.getVariantData().filter((v) => picker.querySelector(':checked').value === v.option1);
        updateVariantStatuses(sameFirstOption, [...picker.querySelectorAll('.select--small')]);
      }

      const variant = this.getVariantData().find(
        (v) => !v.options.map((value, index) => options[index] === value).includes(false)
      );
      if (!variant) {
        if (this.skipNonExistent) findAvailableVariant(picker, options, false, true, this.skipUnavailable);
        return;
      }
      if (this.skipUnavailable && !variant.available) {
        if (findAvailableVariant(picker, options, false, true, true)) return;
      }

      picker.dataset.selectedId = variant.id;
      const selectIndex = picker.dataset.selectIndex;
      const breakElement = picker.closest('.quantity-break');
      const inputKey = breakElement.dataset.input;
      this.selectedVariants[inputKey][selectIndex] = variant.id;
      this.formVariants = this.selectedVariants[inputKey];
      this.updateMedia(variant);
      if (!this.updatePrices) return;

      let price = 0;
      let comparePrice = 0;
      const quantity = parseFloat(breakElement.dataset.quantity);
      const percentageLeft = parseFloat(breakElement.dataset.percentageLeft);
      const fixedDiscount = parseFloat(breakElement.dataset.fixedDiscount);
      for (let i = 0; i < quantity; i++) {
        const id = parseInt(this.selectedVariants[inputKey][i]);
        const selected = this.getVariantData().find((v) => parseInt(v.id) === id);
        if (!selected) return;
        price += selected.price;
        comparePrice +=
          selected.compare_at_price && selected.compare_at_price > selected.price ? selected.compare_at_price : selected.price;
      }
      price = price * percentageLeft - fixedDiscount;
      const saved = comparePrice - price;
      const replacements = {
        '[quantity]': quantity,
        '[price]': formatMoney(price, this.moneyFormat, true),
        '[compare_price]': formatMoney(comparePrice, this.moneyFormat, true),
        '[amount_saved]': formatMoney(saved, this.moneyFormat, true),
        '[amount_saved_rounded]': formatMoney(Math.round(saved / 100) * 100, this.moneyFormat, true),
        '[price_each]': formatMoney(price / quantity, this.moneyFormat, true),
        '[compare_price_each]': formatMoney(comparePrice / quantity, this.moneyFormat, true),
      };
      breakElement.querySelectorAll('.variant-price-update').forEach((element) => {
        let text = element.dataset.text;
        for (const [token, value] of Object.entries(replacements)) text = text.replace(token, value);
        element.innerHTML = text;
      });
      breakElement.querySelector('.quantity-break__compare-price')?.classList.toggle('hidden', !(comparePrice > price));
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.jsonData.textContent);
      return this.variantData;
    }

    initVariants() {
      if (!this.hasVariants) return;
      this.labels.forEach((label) => {
        if (!label.querySelector('.quantity-break__variants')) return;
        const ids = [];
        label.querySelectorAll('.quantity-break__selector-item').forEach((item) => ids.push(item.dataset.selectedId));
        this.selectedVariants[label.dataset.input] = ids;
      });
      this.formVariants = [];
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      document
        .querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`)
        .forEach((gallery) => gallery.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true));
    }

    handleChange(event) {
      const quantity = parseInt(event.target.value);
      this.selectedQuantity = quantity;
      if (this.hasVariants) this.formVariants = this.selectedVariants[event.target.dataset.input];
      this.quantityGifts?.unlockGifts(quantity);
    }
  }
  customElements.define('quantity-breaks', QuantityBreaks);

  /* ---------- Quantity gifts ---------- */

  class QuantityGifts extends HTMLElement {
    constructor() {
      super();
      this.gifts = this.querySelectorAll('.quantity-gift');
      this.quantityBreaks = document.getElementById(`quantity-breaks-${this.dataset.section}`);
      this.quantitySelector = document.getElementById(`Quantity-Form--${this.dataset.section}`);
      this.unlockedItems = [];
      this.initUnlock();
    }

    initUnlock() {
      let quantity = 1;
      if (this.quantityBreaks) {
        quantity = parseInt(this.quantityBreaks.selectedQuantity);
      } else if (this.quantitySelector) {
        quantity = parseInt(this.quantitySelector.querySelector('input[name="quantity"]').value);
      }
      this.unlockGifts(quantity);
    }

    unlockGifts(quantity) {
      this.unlockedItems = [];
      this.gifts.forEach((gift) => {
        const unlocked = parseInt(gift.dataset.quantity) <= quantity;
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
        gift.dataset.unlocked = unlocked ? 'true' : 'false';
        if (unlocked && gift.dataset.product) this.unlockedItems.unshift(gift.dataset.product);
      });
    }
  }
  customElements.define('quantity-gifts', QuantityGifts);

  /* ---------- Main bundle offer ---------- */

  class MainBundleOffer extends HTMLElement {
    constructor() {
      super();
      this.offerItems = this.querySelectorAll('.main-offer-item');
      this.updatePrices = this.dataset.updatePrices === 'true';
      if (!this.updatePrices) return;
      this.priceSpan = this.querySelector('.bundle-deals__total-price-js');
      this.comparePriceSpan = this.querySelector('.bundle-deals__total-compare-price-js');
      this.percentageLeft = parseFloat(this.dataset.percentageLeft);
      this.fixedDiscount = parseFloat(this.dataset.fixedDiscount);
      this.moneyFormat = this.dataset.moneyFormat;
    }

    updateTotalPrices() {
      if (!this.updatePrices) return;
      let price = 0;
      let comparePrice = 0;
      this.offerItems.forEach((item) => {
        price += parseInt(item.price);
        comparePrice += parseInt(item.comparePrice);
      });
      price = price * this.percentageLeft - this.fixedDiscount;
      displayPrices(price, comparePrice, this.priceSpan, this.comparePriceSpan, this.moneyFormat);
    }
  }
  customElements.define('main-bundle-offer', MainBundleOffer);

  /* ---------- Custom product field (line item property) ---------- */

  class CustomProductField extends HTMLElement {
    connectedCallback() {
      this.fieldName = this.dataset.name;
      this.input = this.querySelector('[type="text"], [type="number"], textarea');
      this.inputRadios = this.querySelectorAll('[type="radio"]');
      this.select = this.querySelector('.select__select');
      this.productForm = document.getElementById(`product-form-${this.dataset.section}`);
      this.prevValue = this.dataset.defaultValue;
      this.isRequired = this.dataset.required === 'true';
      this.isText = !(this.dataset.type === 'select' || this.dataset.type === 'pills');
      this.notMain = this.dataset.notMain === 'true';
      if (!this.productForm) return;

      this.createInputs();

      if (this.isRequired && this.isText) {
        this.isValid = true;
        this.atcButtons = this.notMain
          ? document.querySelectorAll(`.featured-product-atc-${this.dataset.section}`)
          : document.querySelectorAll('.main-product-atc');
        this.mainAtcButton = this.productForm.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        this.mainAtcBtnLabel = this.mainAtcButton.querySelector('.main-atc__label');
        this.mainAtcBtnError = this.mainAtcButton.querySelector('.main-atc__error');
        this.atcErrorMsg = this.dataset.atcErrorMsg || '';
        this.mainAtcButton.dataset.requiredFields = parseInt(this.mainAtcButton.dataset.requiredFields) + 1;
        if (this.mainAtcBtnError) this.mainAtcBtnError.innerHTML = this.atcErrorMsg;
        this.applyStickyAtcError = this.dataset.applyStickyAtcError === 'true';
        this.stickyAtcButton = document.querySelector(`#sticky-atc-${this.dataset.section}`);
        if (this.applyStickyAtcError && this.stickyAtcButton) {
          this.stickyAtcBtnLabel = this.stickyAtcButton.querySelector('.sticky-atc__label');
          this.stickyAtcBtnError = this.stickyAtcButton.querySelector('.sticky-atc__error');
          if (this.stickyAtcBtnError) this.stickyAtcBtnError.innerHTML = this.atcErrorMsg;
        }
        this.validateValue(this.prevValue, null);
      }

      this.input?.addEventListener('input', this.handleChange.bind(this));
      this.inputRadios.forEach((radio) => radio.addEventListener('input', this.handleChange.bind(this)));
      this.select?.addEventListener('change', this.handleChange.bind(this));
    }

    handleChange(event) {
      const value = event.target.value.trim();
      if (event.target.checkValidity()) {
        this.prevValue = value;
      } else {
        event.target.value = this.prevValue;
        return;
      }
      this.dataset.value = value;
      this.productFormInput.value = value;
      if (this.isRequired && this.isText) this.validateValue(value, event.target);
    }

    validateValue(value, target) {
      const valid = (value || '').length > 0;
      if (valid === this.isValid) return;
      this.isValid = valid;

      if (target) {
        target.classList.toggle('input--error', !valid);
        this.mainAtcButton.dataset.validFields = parseInt(this.mainAtcButton.dataset.validFields) + (valid ? 1 : -1);
      }

      const allValid = this.mainAtcButton.dataset.validFields === this.mainAtcButton.dataset.requiredFields;
      const unavailable = this.mainAtcButton.dataset.unavailable === 'true';
      this.atcButtons.forEach((button) =>
        allValid && !unavailable ? button.removeAttribute('disabled') : button.setAttribute('disabled', '')
      );

      if (this.atcErrorMsg.length === 0) return;
      const show = (element, visible) => {
        if (element) element.style.display = visible ? '' : 'none';
      };
      show(this.mainAtcBtnLabel, allValid);
      show(this.mainAtcBtnError, !allValid);
      if (this.applyStickyAtcError && this.stickyAtcButton) {
        show(this.stickyAtcBtnLabel, allValid);
        show(this.stickyAtcBtnError, !allValid);
      }
    }

    createInputs() {
      this.productFormInput = document.createElement('input');
      this.productFormInput.setAttribute('type', 'hidden');
      this.productFormInput.setAttribute('name', `properties[${this.fieldName}]`);
      this.productFormInput.value = this.dataset.defaultValue || '';
      this.productForm.appendChild(this.productFormInput);
    }
  }
  customElements.define('custom-product-field', CustomProductField);

  /* ---------- Quantity break upsells / gifts ---------- */

  class QuantityBreakUpsells extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const upsell = event.target.closest('.quantity-break__upsell[data-toggle="true"]');
        if (!upsell || !this.contains(upsell)) return;
        // Toggling an add-on must not also switch the selected offer.
        event.preventDefault();
        event.stopPropagation();
        const selected = upsell.dataset.selected !== 'true';
        upsell.dataset.selected = String(selected);
        if (this.dataset.saveLocalStorage === 'true') this.store(upsell, selected);
      });
      if (this.dataset.saveLocalStorage === 'true') this.restore();
    }

    storageKey(upsell) {
      return `sh-qb-upsell-${this.dataset.mainProduct}-${upsell.dataset.handle}`;
    }

    store(upsell, selected) {
      try {
        localStorage.setItem(this.storageKey(upsell), selected ? '1' : '0');
      } catch (error) {
        /* storage unavailable */
      }
    }

    restore() {
      this.querySelectorAll('.quantity-break__upsell[data-toggle="true"]').forEach((upsell) => {
        let value = null;
        try {
          value = localStorage.getItem(this.storageKey(upsell));
        } catch (error) {
          return;
        }
        if (value === null) return;
        upsell.dataset.selected = value === '1' ? 'true' : 'false';
      });
    }

    /** Variant ids of the add-ons that ride along with this offer. */
    selectedIds() {
      return Array.from(this.querySelectorAll('.quantity-break__upsell[data-selected="true"]'))
        .map((upsell) => upsell.dataset.product)
        .filter(Boolean);
    }
  }
  customElements.define('quantity-break-upsells', QuantityBreakUpsells);

  /* ---------- Subscription widget (selling plans) ---------- */

  class SubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.init();
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.dataset.section) return;
        const updated = event.data.html.getElementById(this.id);
        if (!updated) return;
        const selectedType = this.selectedType;
        this.innerHTML = updated.innerHTML;
        Object.assign(this.dataset, updated.dataset);
        this.init(selectedType);
      });
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
    }

    init(keepType) {
      this.isCheckbox = this.dataset.isCheckbox === 'true';
      this.moneyFormat = this.dataset.moneyFormat;
      this.boxes = this.querySelectorAll('.subscription__box');
      this.boxes.forEach((box) => box.addEventListener('click', this.onBoxClick.bind(this)));
      this.frequencyInputs = this.querySelectorAll('.subscription__frequency select, .subscription__frequency-input');
      this.frequencyInputs.forEach((input) => input.addEventListener('change', () => this.update()));

      let type = keepType || this.dataset.preselected || 'one_time';
      if (!this.querySelector(`.subscription__box[data-type="${type}"]`) && !this.isCheckbox) type = 'subscription';
      this.select(type);
    }

    onBoxClick(event) {
      if (event.target.closest('select, option, .subscription__frequency')) return;
      const type = event.currentTarget.dataset.type;
      if (this.isCheckbox) {
        this.select(this.selectedType === 'subscription' ? 'one_time' : 'subscription');
      } else {
        this.select(type);
      }
    }

    select(type) {
      this.selectedType = type;
      this.boxes.forEach((box) => {
        const selected = box.dataset.type === type;
        box.dataset.selected = String(selected);
        box.classList.toggle('subscription__box--selected', selected);
      });
      this.update();
    }

    selectedPlan() {
      const select = this.querySelector('.subscription__frequency select');
      if (select) return select.options[select.selectedIndex];
      return this.querySelector('.subscription__frequency-input:checked');
    }

    planId() {
      const plan = this.selectedPlan();
      return plan?.dataset.sellingPlanId || this.dataset.firstSellingPlanId || '';
    }

    sellingPlanInput() {
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!form) return null;
      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      return input;
    }

    update() {
      const subscribed = this.selectedType === 'subscription';
      const input = this.sellingPlanInput();
      if (input) {
        input.value = subscribed ? this.planId() : '';
        input.disabled = !subscribed;
      }
      this.updatePrices();
      this.dispatchEvent(new CustomEvent('subscription:change', { bubbles: true, detail: { subscribed, sellingPlan: this.planId() } }));
    }

    updatePrices() {
      const plan = this.selectedPlan();
      if (!plan || !this.moneyFormat) return;
      const price = parseInt(plan.dataset.price);
      let compare = parseInt(plan.dataset.comparePrice) || parseInt(this.dataset.baseComparePrice) || price;
      if (compare < price) compare = price;
      const saved = Math.max(0, compare - price);
      const percentage = compare > 0 ? Math.floor((saved * 100) / compare) : 0;
      const money = (cents) => formatMoney(cents, this.moneyFormat, true);
      const replacements = {
        '[price]': money(price),
        '[compare_price]': money(compare),
        '[amount_saved]': money(saved),
        '[amount_saved_rounded]': money(Math.round(saved / 100) * 100),
        '[percentage_saved]': `${percentage}%`,
        '[subscription_name]': this.dataset.subscriptionName || '',
      };
      this.querySelectorAll('.sub-price-update').forEach((element) => {
        let text = element.dataset.text || '';
        for (const [token, value] of Object.entries(replacements)) text = text.split(token).join(value);
        element.innerHTML = text;
      });

      if (this.dataset.updatePrices !== 'true') return;
      const priceContainer = document.getElementById(`price-${this.dataset.section}`);
      const regular = priceContainer?.querySelector('.price-item--sale, .price-item--regular');
      if (!regular) return;
      if (!this.originalPriceMarkup) this.originalPriceMarkup = regular.innerHTML;
      regular.innerHTML = this.selectedType === 'subscription' ? money(price) : this.originalPriceMarkup;
    }
  }
  customElements.define('shrine-subscription-widget', SubscriptionWidget);

  /* ---------- Sticky add to cart ---------- */

  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.isAfterScroll = this.dataset.afterScroll === 'true';
      this.isScrollBtn = this.dataset.scrollBtn === 'true';
      this.mainAtcBtn = document.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
      this.floatingBtns = document.querySelectorAll('.floating-btn');
      this.footerSpacing();

      if (this.isAfterScroll) {
        if (this.mainAtcBtn) {
          this.checkATCScroll();
          document.addEventListener('scroll', this.checkATCScroll.bind(this), { passive: true });
        }
      } else {
        this.floatingBtns.forEach((button) => button.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`));
      }

      if (this.isScrollBtn) {
        this.scrollBtn = this.querySelector('.sticky-atc__scroll-btn');
        const destination = (this.dataset.scrollDestination || '').replace('id', this.dataset.section);
        this.scrollDestination = destination ? document.querySelector(destination) : null;
        if (this.scrollBtn && this.scrollDestination) this.scrollBtn.addEventListener('click', this.handleScrollBtn.bind(this));
      }
    }

    checkATCScroll() {
      this.scrolledPast = window.scrollY > this.mainAtcBtn.offsetTop + this.mainAtcBtn.offsetHeight;
      this.style.transform = this.scrolledPast ? 'none' : '';
      this.floatingBtns.forEach((button) =>
        button.style.setProperty('--sticky-atc-offset', this.scrolledPast ? `${this.offsetHeight}px` : '0px')
      );
    }

    handleScrollBtn() {
      const stickyHeader = document.querySelector('sticky-header');
      const headerHeight = stickyHeader ? stickyHeader.clientHeight : 0;
      window.scrollTo({ top: this.scrollDestination.offsetTop - headerHeight - 15, behavior: 'smooth' });
    }

    footerSpacing() {
      if (document.querySelector('.sticky-atc-footer-spacer')) return;
      const spacer = document.createElement('div');
      spacer.className = 'sticky-atc-footer-spacer';
      spacer.style.height = `${this.clientHeight - 1}px`;
      spacer.style.display = 'block';
      const footer = document.querySelector('.footer');
      if (footer) spacer.style.background = window.getComputedStyle(footer).background;
      document.body.appendChild(spacer);
    }
  }
  customElements.define('sticky-atc', StickyAtc);
})();
